import type { CycleCountScopeType } from '@rematter/frontend-lib-api-client';

import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

const cycleCountTaskReturnFields = {
  id: true,
  taskNumber: true,
  facilityId: true,
  status: true,
  countType: true,
  assignedTo: true,
  isBlindCount: true,
  isFrozen: true,
  dueDate: true,
  startedAt: true,
  completedAt: true,
  approvedAt: true,
  approvedBy: true,
  notes: true,
  timestamps: {
    createdAt: true,
    updatedAt: true,
  },
} as const;

export const startCycleCountTaskAtom = createMutationAtomFactory(
  'inventory.startCycleCountTask',
  (params: { id: string }) => ({
    inventoryCycleCountTaskStart: {
      __args: { id: params.id },
      ...cycleCountTaskReturnFields,
    },
  }),
);

export const completeCycleCountTaskAtom = createMutationAtomFactory(
  'inventory.completeCycleCountTask',
  (params: { id: string }) => ({
    inventoryCycleCountTaskComplete: {
      __args: { id: params.id },
      ...cycleCountTaskReturnFields,
    },
  }),
);

export const approveCycleCountTaskAtom = createMutationAtomFactory(
  'inventory.approveCycleCountTask',
  (params: { id: string }) => ({
    inventoryCycleCountTaskApprove: {
      __args: { id: params.id },
      ...cycleCountTaskReturnFields,
    },
  }),
);

export const cancelCycleCountTaskAtom = createMutationAtomFactory(
  'inventory.cancelCycleCountTask',
  (params: { id: string; reason?: string }) => ({
    inventoryCycleCountTaskCancel: {
      __args: { id: params.id, reason: params.reason },
      ...cycleCountTaskReturnFields,
    },
  }),
);

export const reopenCycleCountTaskAtom = createMutationAtomFactory(
  'inventory.reopenCycleCountTask',
  (params: { id: string }) => ({
    inventoryCycleCountTaskReopen: {
      __args: { id: params.id },
      ...cycleCountTaskReturnFields,
    },
  }),
);

export const createCycleCountTaskAtom = createMutationAtomFactory(
  'inventory.createCycleCountTask',
  (params: {
    facilityId: string;
    countType: CycleCountScopeType;
    isBlindCount?: boolean;
    dueDate?: string;
    notes?: string;
    defaultTolerancePercentage?: number;
  }) => ({
    inventoryCycleCountTaskCreate: {
      __args: {
        input: {
          facilityId: params.facilityId,
          taskNumber: '',
          countType: params.countType,
          isBlindCount: params.isBlindCount ?? false,
          dueDate: params.dueDate,
          notes: params.notes,
          defaultTolerancePercentage: params.defaultTolerancePercentage,
        },
      },
      ...cycleCountTaskReturnFields,
    },
  }),
);

const cycleCountLineReturnFields = {
  id: true,
  cycleCountTaskId: true,
  itemId: true,
  zoneId: true,
  expectedQuantity: true,
  expectedQuantityUnit: true,
  countedQuantity: true,
  countedQuantityUnit: true,
  varianceQuantity: true,
  variancePercentage: true,
  toleranceThreshold: true,
  withinTolerance: true,
  adjustmentStatus: true,
  adjustmentMovementId: true,
  unitCostOverride: true,
  countedBy: true,
  countedAt: true,
  recountRequested: true,
  notes: true,
  timestamps: {
    createdAt: true,
    updatedAt: true,
  },
} as const;

export const recordLineCountAtom = createMutationAtomFactory(
  'inventory.recordLineCount',
  (params: {
    id: string;
    countedQuantity: number;
    countedQuantityUnit?: string;
  }) => ({
    cycleCountLineRecordCount: {
      __args: {
        id: params.id,
        countedQuantity: params.countedQuantity,
        countedQuantityUnit: params.countedQuantityUnit,
      },
      ...cycleCountLineReturnFields,
    },
  }),
);

export const approveLineAdjustmentAtom = createMutationAtomFactory(
  'inventory.approveLineAdjustment',
  (params: { id: string; unitCostOverride?: number }) => ({
    cycleCountLineApproveAdjustment: {
      __args: {
        id: params.id,
        unitCostOverride: params.unitCostOverride,
      },
      ...cycleCountLineReturnFields,
    },
  }),
);

export const rejectLineAdjustmentAtom = createMutationAtomFactory(
  'inventory.rejectLineAdjustment',
  (params: { id: string }) => ({
    cycleCountLineRejectAdjustment: {
      __args: { id: params.id },
      ...cycleCountLineReturnFields,
    },
  }),
);

export const requestLineRecountAtom = createMutationAtomFactory(
  'inventory.requestLineRecount',
  (params: { id: string }) => ({
    cycleCountLineRequestRecount: {
      __args: { id: params.id },
      ...cycleCountLineReturnFields,
    },
  }),
);
